import React, { useState, useEffect } from "react";
import { Bell, FileText, Home, Package, User, BarChart2, LogOut, Building, Menu, X } from "lucide-react";
import { PieChart, Pie, Cell, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import CompanySelector from './CompanySelector';

// SAP Vendor Portal Dashboard
// Tables referenced: EKKO, EKPO, BSEG, BSAK, BSIK, LFBK
export default function VendorFinalDashboard({ vendorData, setVendorData, vendorFiles, setVendorFiles, api }) {
  const [active, setActive] = useState("home");

  // ---------------- Existing Session & Company Logic ----------------
  const [companyLogo, setCompanyLogo] = useState('Main.png');
  const [companyName, setCompanyName] = useState('');
  const [hasMultipleCompanies, setHasMultipleCompanies] = useState(false);
  const [showCompanySelector, setShowCompanySelector] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Default 



  useEffect(() => {
    // Fetch active company details
    (async () => {
      try {
        const res = await api.get('/api/active-company/');
        const hasMultiple = res.data.has_multiple_companies || false;
        const sessionSelected = sessionStorage.getItem('vendor_company_selected');

        if (res.data.company) {
          if (hasMultiple && !sessionSelected) {
            setHasMultipleCompanies(true);
            setShowCompanySelector(true);
          } else {
            const logo = res.data.company.logo || 'Main.png';
            setCompanyLogo(logo);
            setCompanyName(res.data.company.name);
            setHasMultipleCompanies(hasMultiple);
            setShowCompanySelector(false);
          }
          localStorage.removeItem('company_selector_error');
        } else {
          setHasMultipleCompanies(hasMultiple);
          setShowCompanySelector(true);
        }
      } catch (err) {
        console.error("Error fetching active company:", err);
        if (err.response?.status === 404 || err.response?.status === 500) {
          setShowCompanySelector(true);
        } else {
          const hasError = localStorage.getItem('company_selector_error') === 'true';
          if (hasError) setShowCompanySelector(true);
          else setCompanyLogo('Main.png');
        }
      }
    })();

    // Check session validity
    const sessionCheckInterval = setInterval(async () => {
      try {
        const sessionRes = await api.get('/api/check-session/');
        if (!sessionRes.data?.valid) handleLogout();
      } catch (err) {
        if (err.response?.status === 401) handleLogout();
      }
    }, 30000);

    return () => clearInterval(sessionCheckInterval);
  }, [api]);

  const handleLogout = async () => {
    try { await api.post('/api/logout/'); } catch (err) { }
    localStorage.removeItem('access');
    localStorage.removeItem('refresh');
    localStorage.removeItem('session_id');
    localStorage.removeItem('vendor_login_type');
    sessionStorage.removeItem('vendor_company_selected');
    window.location.href = '/';
  };

  // ---------------- Company Selector Overlay ----------------
  if (showCompanySelector) {
    return (
      <div style={{ position: 'fixed', top: 0, left: 0, width: '100%', height: '100%', zIndex: 1000, background: 'white' }}>
        <CompanySelector
          token={localStorage.getItem('access')}
          onCompanySelected={(company) => {
            sessionStorage.setItem('vendor_company_selected', 'true');
            setShowCompanySelector(false);
            window.location.reload();
          }}
        />
      </div>
    );
  }

  // ---------------- Main Dashboard Render ----------------
  return (
    <div className="min-h-screen relative scroll-smooth flex flex-col hide-scrollbar pt-[80px]" style={{ background: 'linear-gradient(90deg, #552a59, #010c49, #213293)', backgroundAttachment: 'fixed' }}>
      {/* Top Header - Spans full width, sticky at the very top */}
      <header
        className="flex items-center justify-between p-4 shadow z-50 text-white fixed top-0 left-0 w-full"
        style={{ background: 'linear-gradient(90deg, #552a59, #010c49, #213293)' }}
      >
        <div className="flex items-center">
          <button
            onClick={() => setIsSidebarOpen(!isSidebarOpen)}
            className="text-white hover:bg-white/10 p-1 rounded transition relative z-20"
          >
            <Menu size={24} />
          </button>
          <img draggable={false}
            src={process.env.PUBLIC_URL + '/' + companyLogo}
            alt="Logo"
            className="pointer-events-none"
            style={{
              maxHeight: companyLogo.includes('STL_Logo') ? '57px' : '55px',
              marginLeft: companyLogo.includes('STL_Logo') ? '-25px' : '-12px',
              marginTop: companyLogo.includes('STL_Logo') ? '-4px' : '0px',
              objectFit: 'contain',
              position: 'relative',
              zIndex: 10
            }}
            onError={(e) => { e.target.src = process.env.PUBLIC_URL + '/Main.png'; }}
          />
        </div>
      </header>

      {/* Style to hide sidebar scrollbar and ensure background coverage */}
      <style>{`
        .hide-scrollbar::-webkit-scrollbar { display: none; }
        .hide-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
        body { background: linear-gradient(90deg, #552a59, #010c49, #213293) fixed !important; margin: 0; }
      `}</style>

      {/* Body Layout */}
      <div className="flex flex-1 relative">
        {/* Sidebar - Full Height Overlay on mobile, Sticky on desktop */}
        <aside
          className={`
            fixed md:fixed top-0 md:top-[80px] left-0 z-[60] md:z-40 shadow-2xl md:shadow-lg flex flex-col transition-all duration-300 ease-in-out
            h-full md:h-[calc(100vh-80px)] 
            ${isSidebarOpen ? "w-72 md:w-64" : "w-0"} 
            text-white overflow-hidden
          `}
          style={{ background: 'linear-gradient(90deg, #552a59, #010c49, #213293)', borderRight: '1px solid rgba(255,255,255,0.1)' }}
        >
          {/* Mobile Sidebar Header */}
          <div className="flex md:hidden items-center justify-between p-6 border-b border-white/10 min-w-[288px]">
            <img draggable={false}
              src={process.env.PUBLIC_URL + '/' + companyLogo}
              alt="Logo"
              style={{ maxHeight: '50px', objectFit: 'contain' }}
              onError={(e) => { e.target.src = process.env.PUBLIC_URL + '/Main.png'; }}
            />
            <button
              onClick={() => setIsSidebarOpen(false)}
              className="p-3 hover:bg-white/10 rounded-full transition"
            >
              <X size={28} />
            </button>
          </div>

          <nav className="flex flex-col gap-2 p-4 flex-1 overflow-y-auto min-w-[288px] md:min-w-[256px] hide-scrollbar">
            <SidebarItem icon={<Home size={22} />} label="Dashboard" active={active === "home"} onClick={() => { setActive("home"); if (window.innerWidth < 768) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<Package size={22} />} label="PO Management" active={active === "po"} onClick={() => { setActive("po"); if (window.innerWidth < 768) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<FileText size={22} />} label="Service Entry" active={active === "ses"} onClick={() => { setActive("ses"); if (window.innerWidth < 768) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<BarChart2 size={22} />} label="Reports" active={active === "reports"} onClick={() => { setActive("reports"); if (window.innerWidth < 768) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<Bell size={22} />} label="Payments & GR" active={active === "payment"} onClick={() => { setActive("payment"); if (window.innerWidth < 768) setIsSidebarOpen(false); }} />
            <SidebarItem icon={<User size={22} />} label="Manage Profile" active={active === "profile"} onClick={() => { setActive("profile"); if (window.innerWidth < 768) setIsSidebarOpen(false); }} />
          </nav>

          {/* Bottom Actions */}
          <div className="p-4 border-t border-white/10 space-y-2 bg-black/20 min-w-[288px] md:min-w-[256px]">
            {hasMultipleCompanies && (
              <button
                onClick={() => setShowCompanySelector(true)}
                className="flex items-center gap-3 px-4 py-4 w-full text-left text-white/90 hover:bg-white/10 rounded-lg transition text-base font-medium"
              >
                <Building size={20} /> Switch Company
              </button>
            )}
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-4 w-full text-left text-red-300 hover:bg-red-500/20 hover:text-red-100 rounded-lg transition text-base font-medium"
            >
              <LogOut size={20} /> Logout
            </button>
          </div>
        </aside>

        {/* Dashboard Content - Grows to fill space */}
        <main className={`flex-1 p-4 md:p-8 min-w-0 transition-all duration-300 ${isSidebarOpen ? "md:ml-64" : "md:ml-0"}`}>
          <div className="max-w-full">
            {active === "home" && <HomeDashboard />}
            {active === "po" && <POManagement api={api} />}
            {active === "ses" && <ServiceEntry />}
            {active === "reports" && <Reports />}
            {active === "payment" && <PaymentGR />}
            {active === "profile" && <ManageProfile />}
          </div>
        </main>
      </div>

      <div className="w-full flex justify-center items-center py-4 mt-auto bg-transparent z-50">
        <span className="font-semibold text-white mr-2">Powered by</span>
        <img draggable={false} src={process.env.PUBLIC_URL + '/Main.png'} alt="STL-DIGITAL" className="h-8" />
      </div>

      {/* Overlay backdrop for mobile */}
      {isSidebarOpen && (
        <div
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-[55] md:hidden animate-fadeIn"
          onClick={() => setIsSidebarOpen(false)}
        ></div>
      )}
    </div>
  );
}

// ---------------- Sub Components (Unchanged logic, just maintaining file completeness) ----------------

function SidebarItem({ icon, label, active, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-left transition w-full ${active
        ? "bg-white/20 text-white font-semibold backdrop-blur-sm border-l-4 border-white shadow-sm"
        : "text-gray-300 hover:bg-white/10 hover:text-white"
        }`}
    >
      {icon}
      {label}
    </button>
  );
}

function HomeDashboard() {
  const payments = [
    { check: "CHK889900", date: "02-Feb-2026", amount: "₹78,000" },
    { check: "CHK778811", date: "20-Jan-2026", amount: "₹1,24,000" },
  ];

  const alerts = [
    "New PO Issued – 4500007890",
    "Invoice INV-234 Rejected",
    "GR Posted for PO 4500007811",
  ];
  const [drill, setDrill] = useState(null);

  const kpis = {
    openPOs: 14,
    closedPOs: 32,
    pendingInvoices: 6,
    closedInvoices: 18,
    grLast30Days: 11,
  };

  const poStatus = [
    { name: "New", value: 5 },
    { name: "Acknowledged", value: 6 },
    { name: "Closed", value: 3 },
  ];

  const invoiceAging = [
    { bucket: "0–30 Days", amount: 180000 },
    { bucket: "31–60 Days", amount: 92000 },
    { bucket: "61–90 Days", amount: 46000 },
  ];

  const grTimeline = [
    { stage: "PO", value: "4500007890" },
    { stage: "GR", value: "GR456789" },
    { stage: "Invoice", value: "INV-234" },
    { stage: "Payment", value: "UTR998877" },
  ];

  const openPODetails = [
    { PO: "4500007890", Plant: "1000", Value: "₹2,45,000" },
    { PO: "4500007888", Plant: "2000", Value: "₹1,12,400" },
  ];

  return (
    <div className="space-y-6 md:space-y-8">

      {/* UI Changes to linear-gradient(90deg, #552a59, #010c49, #213293)' */}
      {/* <h1 className="text-2xl md:text-3xl font-semibold text-gray-800">Vendor Dashboard</h1> */}
      <h1 className="text-2xl md:text-3xl font-semibold text-white">Vendor Dashboard</h1>

      {/* KPI Tiles */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4 md:gap-6">
        <KPICard title="Open POs" value={kpis.openPOs} onClick={() => setDrill("openPO")} />
        <KPICard title="Closed POs" value={kpis.closedPOs} />
        <KPICard title="Pending Invoices" value={kpis.pendingInvoices} />
        <KPICard title="Closed Invoices" value={kpis.closedInvoices} />
        <div className="col-span-2 md:col-span-1">
          <KPICard title="GRs (30 Days)" value={kpis.grLast30Days} />
        </div>
      </div>

      {/* Middle Section */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
          <h3 className="font-semibold mb-4 text-gray-700">PO Status</h3>
          <ResponsiveContainer width="100%" height={200}>
            <PieChart>
              <Pie data={poStatus} dataKey="value" nameKey="name" outerRadius={70}>
                {poStatus.map((_, i) => (
                  <Cell key={i} fill={["#552a59", "#213293", "#010c49"][i]} />
                ))}
              </Pie>
              <Tooltip />
            </PieChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
          <h3 className="font-semibold mb-4 text-gray-700">Invoice Aging</h3>
          <ResponsiveContainer width="100%" height={200}>
            <BarChart data={invoiceAging}>
              <XAxis dataKey="bucket" />
              <YAxis />
              <Tooltip />
              <Bar dataKey="amount" fill="#552a59" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
          <h3 className="font-semibold mb-4 text-gray-700">GR to Payment Timeline</h3>
          <ul className="space-y-2 text-sm">
            {grTimeline.map((t) => (
              <li key={t.stage} className="flex justify-between">
                <span className="font-medium text-gray-800">{t.stage}</span>
                <span className="text-gray-500">{t.value}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Recent Payments */}
      <div className="bg-white p-6 rounded-xl shadow col-span-2 overflow-x-auto border border-gray-100">
        <h3 className="font-semibold mb-4 text-gray-700">Recent Payments</h3>
        <table className="w-full text-sm min-w-[500px]">
          <thead className="bg-gray-50 text-gray-600">
            <tr>
              <th className="p-3 text-left font-semibold">Check No</th>
              <th className="p-3 text-left font-semibold">Date</th>
              <th className="p-3 text-left font-semibold">Amount</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.check} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="p-3 text-gray-800">{p.check}</td>
                <td className="p-3 text-gray-600">{p.date}</td>
                <td className="p-3 font-medium text-green-600">{p.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Alerts */}
      {drill === "openPO" && (
        <DrillDownTable title="Open PO Details" rows={openPODetails} />
      )}

      <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
        <h3 className="font-semibold mb-3 text-gray-700">Alerts & Notifications</h3>
        <ul className="list-disc ml-5 text-sm space-y-2 text-gray-600">
          {alerts.map((a) => (
            <li key={a}>{a}</li>
          ))}
        </ul>
      </div>
    </div>
  );
}

// PO Management (EKKO / EKPO)
// PO Management (EKKO / EKPO)
function POManagement({ api }) {
  const [selectedPO, setSelectedPO] = useState(null);
  const [poSearch, setPoSearch] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searchedPO, setSearchedPO] = useState(null);

  const fetchPODetails = async () => {
    if (!poSearch) return;
    setLoading(true);
    setError("");
    setSearchedPO(null);

    try {
      // Use the new SAP API
      const response = await api.get(`/api/sap/po-details/?po_number=${poSearch}`);
      if (response.data && response.data.success) {
        const data = response.data.data;
        // Transform API data to component format if needed, or just use it directly
        const mappedPO = {
          po: data.po_number,
          date: data.doc_date,
          plant: data.items.length > 0 ? data.items[0].plant : 'N/A',
          storage: "",
          vendorRef: data.vendor,
          value: data.items.reduce((sum, item) => sum + (parseFloat(item.net_price) * parseFloat(item.quantity)), 0).toFixed(2),
          currency: data.currency,
          status: "Open", // Overall status could be derived, but keeping simple for now
          items: data.items.map(item => {
            // Determine item status
            let itemStatus = "Open";
            if (item.delete_ind) itemStatus = "Deleted";
            else if (item.delivery_complete === "X" || item.final_invoice === "X") itemStatus = "Closed";

            return {
              item: item.po_item,
              mat: item.material,
              desc: item.short_text,
              qty: item.quantity,
              uom: item.unit,
              delDate: item.delivery_date || "N/A",
              status: itemStatus,
              // Keep raw flags if needed
              delete_ind: item.delete_ind,
              delivery_complete: item.delivery_complete,
              final_invoice: item.final_invoice
            };
          })
        };
        setSearchedPO(mappedPO);
        setSelectedPO(mappedPO);
      } else {
        setError("PO not found or SAP error");
      }
    } catch (err) {
      console.error("Error fetching PO:", err);
      setError(err.response?.data?.error || "Failed to fetch PO details");
    } finally {
      setLoading(false);
    }
  };

  const pos = [
    {
      po: "4500007890",
      date: "05-Feb-2026",
      plant: "1000",
      storage: "RM01",
      vendorRef: "V-REF-7788",
      value: "₹2,45,000",
      status: "Open",
      items: [
        { item: "10", mat: "RM-1001", desc: "Steel Rod", qty: 100, uom: "KG", delDate: "15-Feb-2026", status: "Open" },
        { item: "20", mat: "RM-1002", desc: "Aluminium Sheet", qty: 50, uom: "KG", delDate: "18-Feb-2026", status: "Open" },
      ],
    },
    {
      po: "4500007811",
      date: "28-Jan-2026",
      plant: "2000",
      storage: "FG01",
      vendorRef: "V-REF-6622",
      value: "₹1,18,500",
      status: "Closed",
      items: [
        { item: "10", mat: "SRV-2001", desc: "Maintenance Service", qty: 1, uom: "AU", delDate: "20-Jan-2026", status: "Closed" },
      ],
    },
  ];

  const [filterStatus, setFilterStatus] = useState("All");

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">PO Management</h2>

        {/* SAP Live Search */}
        <div className="flex gap-4 mb-6 items-end border-b pb-6">
          <div className="flex-1">
            <label className="block text-sm font-medium text-gray-700 mb-1">Search SAP PO :</label>
            <div className="flex gap-2">
              <input
                value={poSearch}
                onChange={(e) => setPoSearch(e.target.value)}
                placeholder="Enter PO Number (e.g. 1150023250)"
                className="flex-1 border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none"
              />
              <button
                onClick={fetchPODetails}
                disabled={loading}
                className="bg-blue-600 text-white px-4 py-2 rounded hover:bg-blue-700 disabled:opacity-50"
              >
                {loading ? 'Searching...' : 'Search'}
              </button>
            </div>
            {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
          </div>
        </div>


        {/* Dictionary Filters (Client Side) */}
        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
          <input placeholder="Filter Local PO Number" className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
          <input type="date" className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
          <input placeholder="Plant" className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
          <input placeholder="Storage Location" className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none" />
          <select className="border p-2 rounded focus:ring-2 focus:ring-blue-500 outline-none">
            <option>Status</option>
            <option>Open</option>
            <option>Closed</option>
          </select>
        </div>

        {/* PO Listing */}
        <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[800px]">
            <thead className="bg-gray-50 text-gray-600">
              <tr>
                <th className="p-3 text-left font-semibold">PO Number</th>
                <th className="p-3 text-left font-semibold">Doc Date</th>
                <th className="p-3 text-left font-semibold">Vendor Ref</th>
                <th className="p-3 text-left font-semibold">Plant</th>
                <th className="p-3 text-left font-semibold">Total Value</th>
                <th className="p-3 text-left font-semibold">Status</th>
                <th className="p-3 text-left font-semibold">Action</th>
              </tr>
            </thead>
            <tbody>
              {pos.map((p) => (
                <tr key={p.po} className="border-t border-gray-100 hover:bg-gray-50">
                  <td className="p-3 text-blue-600 font-medium cursor-pointer hover:underline" onClick={() => setSelectedPO(p)}>{p.po}</td>
                  <td className="p-3 text-gray-600">{p.date}</td>
                  <td className="p-3 text-gray-600">{p.vendorRef}</td>
                  <td className="p-3 text-gray-600">{p.plant}</td>
                  <td className="p-3 font-medium text-gray-800">{p.value}</td>
                  <td className="p-3"><StatusPill status={p.status} /></td>
                  <td className="p-3 space-x-2">
                    <button className="text-blue-600 hover:text-blue-800 text-xs font-semibold uppercase">View</button>
                    <button className="text-gray-500 hover:text-gray-700 text-xs font-semibold uppercase">PDF</button>
                    {p.status === "Open" && (
                      <button className="text-green-600 hover:text-green-800 text-xs font-semibold uppercase">Acknowledge</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* PO Detail Drawer */}
      {selectedPO && (
        <div className="bg-white rounded-xl shadow p-6 border border-gray-100 animate-fadeIn">
          <div className="flex justify-between items-center mb-4">
            <h3 className="font-semibold text-lg text-gray-800">PO Details – {selectedPO.po}</h3>
            <div className="flex gap-2">
              <select
                className="border p-1 rounded text-sm bg-gray-50 outline-none focus:ring-1 focus:ring-blue-500"
                value={filterStatus}
                onChange={(e) => setFilterStatus(e.target.value)}
              >
                <option value="All">All Items</option>
                <option value="Open">Open Only</option>
                <option value="Closed">Closed Only</option>
              </select>
              <button onClick={() => setSelectedPO(null)} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[600px]">
              <thead className="bg-gray-50 text-gray-600">
                <tr>
                  <th className="p-2 text-left font-semibold">Item</th>
                  <th className="p-2 text-left font-semibold">Material</th>
                  <th className="p-2 text-left font-semibold">Description</th>
                  <th className="p-2 text-left font-semibold">Qty</th>
                  <th className="p-2 text-left font-semibold">UOM</th>
                  <th className="p-2 text-left font-semibold">Delivery Date</th>
                  <th className="p-2 text-left font-semibold">Status</th>
                </tr>
              </thead>
              <tbody>
                {selectedPO.items
                  .filter(i => filterStatus === "All" || i.status === filterStatus)
                  .map((i) => (
                    <tr key={i.item} className="border-t border-gray-100">
                      <td className="p-2 text-gray-600">{i.item}</td>
                      <td className="p-2"><span className="text-gray-800 font-medium bg-gray-50 rounded px-2 py-1">{i.mat}</span></td>
                      <td className="p-2 text-gray-600">{i.desc}</td>
                      <td className="p-2 text-gray-600">{i.qty}</td>
                      <td className="p-2 text-gray-600">{i.uom}</td>
                      <td className="p-2 text-gray-600">{i.delDate}</td>
                      <td className="p-2">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${i.status === 'Open' ? 'bg-green-100 text-green-700' :
                          i.status === 'Closed' ? 'bg-gray-100 text-gray-600' :
                            'bg-red-100 text-red-700'
                          }`}>{i.status}</span>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>

          {selectedPO.status === "Open" && (
            <div className="mt-6 flex flex-col md:flex-row gap-4">
              <button className="bg-green-600 hover:bg-green-700 text-white px-5 py-2 rounded shadow-sm transition">Acknowledge PO</button>
              <button className="bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded shadow-sm transition">Create Service Entry</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// Service Entry (ESLL / ESSR)
// BAPI: BAPI_ENTRYSHEET_CREATE
function ServiceEntry() {
  const servicePOs = [
    {
      po: "4500007811",
      item: "10",
      serviceCode: "SRV-2001",
      description: "Annual Maintenance Service",
      rate: 5000,
      totalQty: 12,
      invoicedQty: 4,
    },
  ];

  const [selectedPO, setSelectedPO] = useState(null);
  const [form, setForm] = useState({
    sesRef: "",
    date: "",
    qty: "",
    attachment: null,
  });

  const remainingQty = selectedPO
    ? selectedPO.totalQty - selectedPO.invoicedQty
    : 0;

  const submitSES = () => {
    alert(
      `SES Submitted for Approval\nPO: ${selectedPO.po}\nQty: ${form.qty}\n(Status: Pending Business Approval)`
    );
  };

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
        <h2 className="text-2xl font-semibold mb-4 text-gray-800">Create Service Entry</h2>

        {/* Select Service PO (EKPO-PSTYP = D) */}
        <select
          className="border p-2 rounded w-full mb-4 bg-gray-50 focus:bg-white focus:ring-2 focus:ring-blue-500 outline-none"
          onChange={(e) =>
            setSelectedPO(servicePOs.find((p) => p.po === e.target.value))
          }
        >
          <option value="">Select Service PO</option>
          {servicePOs.map((p) => (
            <option key={p.po} value={p.po}>
              {p.po} – {p.description}
            </option>
          ))}
        </select>

        {selectedPO && (
          <div className="space-y-4 animate-fadeIn">
            {/* Locked SAP fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <input
                value={selectedPO.po}
                disabled
                className="border p-2 rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                title="PO Number"
              />
              <input
                value={selectedPO.item}
                disabled
                className="border p-2 rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                title="Item Number"
              />
              <input
                value={selectedPO.serviceCode}
                disabled
                className="border p-2 rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                title="Service Code"
              />
              <input
                value={`Rate: ₹${selectedPO.rate}`}
                disabled
                className="border p-2 rounded bg-gray-100 text-gray-500 cursor-not-allowed"
                title="Rate"
              />
            </div>

            {/* Editable fields */}
            <input
              placeholder="SES Reference"
              className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-500 outline-none"
              onChange={(e) => setForm({ ...form, sesRef: e.target.value })}
            />

            <input
              type="date"
              className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-500 outline-none"
              max={new Date().toISOString().split("T")[0]}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />

            <input
              type="number"
              placeholder={`Quantity (Remaining: ${remainingQty})`}
              className="border p-2 rounded w-full focus:ring-2 focus:ring-blue-500 outline-none"
              max={remainingQty}
              onChange={(e) => setForm({ ...form, qty: e.target.value })}
            />

            {/* <div className="file-input-wrapper border p-2 rounded w-full bg-gray-50">
              <input
                type="file"
                accept="application/pdf"
                className="w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-full file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                onChange={handleFileChange}
              />
            </div> */}

            <button
              onClick={submitSES}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded w-full md:w-auto font-medium shadow-sm transition mt-4"
            >
              Submit for Approval
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

// Reports Module (All component code preserved below)
function ReportTable({ title, description, columns, rows, formats }) {
  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3 bg-white shadow-sm hover:shadow-md transition">
      <div className="flex justify-between items-center flex-wrap gap-2">
        <div>
          <h3 className="text-lg font-semibold text-gray-800">{title}</h3>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
        <div className="text-xs bg-blue-50 text-blue-600 px-2 py-1 rounded border border-blue-100">Export: {formats}</div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full border-collapse min-w-[600px]">
          <thead>
            <tr className="bg-gray-50 text-gray-600 border-b">
              {columns.map((c, i) => (
                <th key={i} className="text-left px-3 py-2 font-semibold">{c}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} className="hover:bg-blue-50 border-b last:border-0 border-gray-100">
                {row.map((cell, j) => (
                  <td key={j} className="px-3 py-3 text-sm text-gray-700">{cell}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Reports() {
  const [activeReport, setActiveReport] = useState("po");
  // ... (Data)
  const poStatusData = [
    { po: "4500007890", date: "05-Feb-2026", plant: "1000", value: "₹2,45,000", delivery: "80%" },
    { po: "4500007811", date: "28-Jan-2026", plant: "2000", value: "₹1,18,500", delivery: "100%" },
  ];
  const paymentData = [
    { invoice: "INV-234", payDate: "01-Feb-2026", ref: "UTR998877", amount: "₹78,000" },
    { invoice: "INV-198", payDate: "15-Jan-2026", ref: "UTR887766", amount: "₹1,24,000" },
  ];
  const sesData = [
    { ses: "10001234", po: "4500007811", approvedBy: "Business Head", postDate: "22-Jan-2026" },
    { ses: "10001256", po: "4500007890", approvedBy: "Plant Manager", postDate: "06-Feb-2026" },
  ];

  return (
    <div className="bg-white rounded-xl shadow p-6 space-y-6 border border-gray-100">
      <h2 className="text-2xl font-semibold text-gray-800">Reports</h2>

      {/* Report Selector - Responsive wrap */}
      <div className="flex flex-wrap gap-4 border-b border-gray-200 pb-4">
        <button onClick={() => setActiveReport("po")} className={`px-4 py-2 rounded-lg font-medium transition ${activeReport === "po" ? "bg-blue-600 text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
          PO Status Report
        </button>
        <button onClick={() => setActiveReport("payment")} className={`px-4 py-2 rounded-lg font-medium transition ${activeReport === "payment" ? "bg-blue-600 text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
          Payment Tracker
        </button>
        <button onClick={() => setActiveReport("ses")} className={`px-4 py-2 rounded-lg font-medium transition ${activeReport === "ses" ? "bg-blue-600 text-white shadow-md" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}>
          Service Entry Log
        </button>
      </div>

      {activeReport === "po" && (
        <ReportTable
          title="PO Status Report"
          description="Track all open vs closed Purchase Orders"
          columns={["PO#", "Date", "Plant", "Total Value", "Delivery %"]}
          rows={poStatusData.map((r) => [r.po, r.date, r.plant, r.value, r.delivery])}
          formats="Excel, PDF"
        />
      )}
      {activeReport === "payment" && (
        <ReportTable
          title="Payment Tracker"
          description="Visibility into cleared vs pending payments"
          columns={["Invoice#", "Payment Date", "Reference#", "Amount"]}
          rows={paymentData.map((r) => [r.invoice, r.payDate, r.ref, r.amount])}
          formats="Excel, On-screen"
        />
      )}
      {activeReport === "ses" && (
        <ReportTable
          title="Service Entry Log"
          description="Audit trail of all Service Entry Sheets"
          columns={["SES#", "PO#", "Approved By", "Posting Date"]}
          rows={sesData.map((r) => [r.ses, r.po, r.approvedBy, r.postDate])}
          formats="PDF, Excel"
        />
      )}
    </div>
  );
}

// Payment & GR Visibility
function PaymentGR() {
  const payments = [
    { ref: "UTR998877", date: "01-Feb-2026", amount: "₹78,000" },
    { ref: "UTR887766", date: "15-Jan-2026", amount: "₹1,24,000" },
  ];

  return (
    <div className="bg-white rounded-xl shadow p-6 border border-gray-100">
      <h2 className="text-2xl font-semibold mb-6 text-gray-800">Payments & GR Visibility</h2>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead className="bg-gray-50 text-gray-600 border-b">
            <tr>
              <th className="p-3 font-semibold text-left">Reference</th>
              <th className="p-3 font-semibold text-left">Date</th>
              <th className="p-3 font-semibold text-left">Amount</th>
            </tr>
          </thead>
          <tbody>
            {payments.map((p) => (
              <tr key={p.ref} className="border-t border-gray-100 hover:bg-gray-50">
                <td className="p-3"><span className="text-gray-800 font-mono bg-gray-50 rounded px-2 py-1">{p.ref}</span></td>
                <td className="p-3 text-gray-600">{p.date}</td>
                <td className="p-3 text-gray-800 font-medium">{p.amount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// Manage Profile
function ManageProfile() {
  const [changeRequested, setChangeRequested] = useState(false);

  return (
    <ReAuthWrapper>
      <div className="space-y-6">
        {/* Company Information */}
        <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Company Information</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div>
              <label className="text-sm text-gray-500 block mb-1">Business Name</label>
              <input className="border p-2 rounded w-full bg-gray-50 text-gray-700" defaultValue="ABC Industrial Services Pvt Ltd" />
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">Tax ID (GST/VAT)</label>
              <input className="border p-2 rounded w-full bg-gray-50 text-gray-700" defaultValue="27AAECA1234F1ZP" />
            </div>
            <div>
              <label className="text-sm text-gray-500 block mb-1">Incorporation Date</label>
              <input type="date" className="border p-2 rounded w-full bg-gray-50 text-gray-700" defaultValue="2015-06-18" />
            </div>
          </div>

          {/* Addresses */}
          <h3 className="font-semibold mt-6 mb-2 text-gray-700">Addresses</h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <textarea className="border p-2 rounded bg-gray-50 text-gray-700" placeholder="Primary Office" defaultValue="Mumbai HO" />
            <textarea className="border p-2 rounded bg-gray-50 text-gray-700" placeholder="Warehouse" defaultValue="Bhiwandi WH" />
            <textarea className="border p-2 rounded bg-gray-50 text-gray-700" placeholder="Remittance Address" defaultValue="Mumbai Finance Office" />
          </div>

          <button
            onClick={() => setChangeRequested(true)}
            className="mt-4 bg-blue-600 hover:bg-blue-700 text-white px-5 py-2 rounded shadow-sm transition"
          >
            Submit Change Request
          </button>

          {changeRequested && (
            <div className="mt-4 p-3 bg-orange-50 border border-orange-200 text-orange-700 rounded text-sm">
              Change Request submitted to Master Data Team (BAPI_VENDOR_UPDATE)
            </div>
          )}
        </div>

        {/* Bank Account Details */}
        <div className="bg-white p-6 rounded-xl shadow border border-gray-100">
          <h2 className="text-xl font-semibold mb-4 text-gray-800">Bank Account Details</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <input className="border p-2 rounded bg-gray-50 w-full" placeholder="Bank Name" defaultValue="HDFC Bank" />
            <input className="border p-2 rounded bg-gray-50 w-full" placeholder="Account Number" defaultValue="XXXXXX4321" />
            <input className="border p-2 rounded bg-gray-50 w-full" placeholder="SWIFT / IFSC Code" defaultValue="HDFC0000123" />
            {/* <div className="border p-2 rounded bg-gray-50 w-full">
              <input
                type="file"
                accept="application/pdf"
                className="w-full text-sm text-gray-500"
                onChange={handleFileChange}
              />
            </div> */}
          </div>
          <p className="text-sm text-gray-500 mt-2 italic">
            * Updates will be verified before LFBK update in SAP.
          </p>
        </div>
      </div>
    </ReAuthWrapper>
  );
}

function StatusPill({ status }) {
  const map = {
    Open: "bg-blue-100 text-blue-700 border border-blue-200",
    Closed: "bg-gray-100 text-gray-600 border border-gray-200",
    Rejected: "bg-red-100 text-red-700 border border-red-200",
  };
  return <span className={`px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wide ${map[status]}`}>{status}</span>;
}

function KPICard({ title, value, onClick }) {
  return (
    <div onClick={onClick} className="bg-white rounded-xl shadow p-6 cursor-pointer hover:shadow-lg transition border border-gray-100 group">
      <div className="text-sm text-gray-500 group-hover:text-[#552a59] transition-colors uppercase font-medium tracking-wide">{title}</div>
      <div className="text-3xl font-bold text-gray-800 mt-2">{value}</div>
    </div>
  );
}

function DrillDownTable({ title, rows }) {
  return (
    <div className="bg-white p-6 rounded-xl shadow border border-gray-100 animate-slideDown">
      <h3 className="font-semibold mb-4 text-gray-800">{title}</h3>
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[500px]">
          <thead className="bg-gray-50 text-gray-600 border-b">
            <tr>
              {Object.keys(rows[0]).map((k) => (
                <th key={k} className="p-2 text-left font-semibold uppercase text-xs tracking-wider">{k}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-gray-100 hover:bg-gray-50">
                {Object.values(r).map((v, idx) => (
                  <td key={idx} className="p-2 text-gray-700">{v}</td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}


// Re-auth Modal Wrapper (Password / 2FA UX)
function ReAuthWrapper({ children }) {
  const [verified, setVerified] = useState(false);

  if (!verified) {
    return (
      <div className="bg-white p-8 rounded-xl shadow text-center max-w-md mx-auto mt-10 border border-gray-100">
        <div className="bg-blue-50 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 text-blue-600">
          <User size={32} />
        </div>
        <h3 className="font-semibold text-xl mb-2 text-gray-800">Security Check</h3>
        <p className="text-gray-500 text-sm mb-6">Please verify your identity to access sensitive profile data.</p>
        <input type="password" placeholder="Enter Password" className="border p-3 rounded w-full mb-4 focus:ring-2 focus:ring-blue-500 outline-none" />
        <button onClick={() => setVerified(true)} className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-2 rounded w-full font-medium shadow transition">Confirm Identity</button>
      </div>
    );
  }

  return children;
}
